import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
export default function ContatoPage() {
  const { t } = useTranslation('contato');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // Definir meta tags SEO
    document.title = "Contato | FGT Capital São Paulo - Engenharia Patrimonial";
    
    // Adicionar Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contato - FGT Capital",
      "description": "Entre em contato com a FGT Capital para consultoria em engenharia patrimonial e consórcios regulados em São Paulo.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contato`,
      "mainEntity": {
        "@type": "Organization",
        "name": "FGT Capital",
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "telephone": "+55-11-93032-2408",
            "contactType": "customer service",
            "availableLanguage": "Portuguese",
            "areaServed": "BR"
          },
          {
            "@type": "ContactPoint", 
            "email": "contato@fgt-capital.com",
            "contactType": "customer service",
            "availableLanguage": "Portuguese"
          }
        ],
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "São Paulo",
          "addressRegion": "SP",
          "addressCountry": "BR"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('whatsapp', formData.whatsapp);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6j221ufjdhm2lq4t22g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', whatsapp: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-[#F6F2EC]">

      {/* Hero */}
      <section className="pt-40 pb-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#F97700] mb-6">
              {t('hero.label')}
            </p>
            <h1 className="text-5xl md:text-6xl font-semibold text-[#0B0B0B] leading-[1.1] mb-8">
              {t('hero.title')}
            </h1>
            <p className="text-lg text-[#2E2E2E] leading-relaxed">
              {t('hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="pb-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-semibold text-[#0B0B0B] mb-12">
                {t('info.title')}
              </h2>

              <div className="space-y-10">
                {/* Location */}
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#FFF1E6]">
                      <i className="ri-map-pin-line text-xl text-[#F97700]"></i>
                    </div>
                    <h3 className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0B0B0B]">
                      {t('info.location.label')}
                    </h3>
                  </div>
                  <p className="text-lg text-[#2E2E2E] ml-13">
                    {t('info.location.value')}
                  </p>
                </div>

                {/* WhatsApp */}
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#FFF1E6]">
                      <i className="ri-whatsapp-line text-xl text-[#F97700]"></i>
                    </div>
                    <h3 className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0B0B0B]">
                      {t('info.whatsapp.label')}
                    </h3>
                  </div>
                  <a
                    href="https://wa.me/5511930322408"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg text-[#F97700] hover:text-[#E56E00] transition-colors ml-13 cursor-pointer"
                  >
                    {t('info.whatsapp.value')}
                  </a>
                </div>

                {/* Email */}
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#FFF1E6]">
                      <i className="ri-mail-line text-xl text-[#F97700]"></i>
                    </div>
                    <h3 className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0B0B0B]">
                      {t('info.email.label')}
                    </h3>
                  </div>
                  <a
                    href="mailto:contato@fgt-capital.com"
                    className="text-lg text-[#F97700] hover:text-[#E56E00] transition-colors ml-13 cursor-pointer"
                  >
                    {t('info.email.value')}
                  </a>
                </div>

                {/* Business Hours */}
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#FFF1E6]">
                      <i className="ri-time-line text-xl text-[#F97700]"></i>
                    </div>
                    <h3 className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0B0B0B]">
                      {t('info.hours.label')}
                    </h3>
                  </div>
                  <p className="text-lg text-[#2E2E2E] ml-13">
                    {t('info.hours.value')}
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-[#FBF8F3] border border-[#E7E1D9] p-10">
              <h2 className="text-3xl font-semibold text-[#0B0B0B] mb-8">
                {t('form.title')}
              </h2>

              <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                <div className="space-y-6">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-[#0B0B0B] mb-2">
                      {t('form.fields.name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-[#E7E1D9] text-[#0B0B0B] focus:outline-none focus:border-[#F97700] transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-[#0B0B0B] mb-2">
                      {t('form.fields.email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-[#E7E1D9] text-[#0B0B0B] focus:outline-none focus:border-[#F97700] transition-colors"
                    />
                  </div>

                  {/* WhatsApp */}
                  <div>
                    <label htmlFor="whatsapp" className="block text-sm font-semibold text-[#0B0B0B] mb-2">
                      {t('form.fields.whatsapp')}
                    </label>
                    <input
                      type="tel"
                      id="whatsapp"
                      name="whatsapp"
                      value={formData.whatsapp}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-[#E7E1D9] text-[#0B0B0B] focus:outline-none focus:border-[#F97700] transition-colors"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-[#0B0B0B] mb-2">
                      {t('form.fields.message')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 bg-white border border-[#E7E1D9] text-[#0B0B0B] focus:outline-none focus:border-[#F97700] transition-colors resize-none"
                    ></textarea>
                    <p className="text-xs text-[#2E2E2E] mt-2">
                      {formData.message.length}/500 {t('form.fields.characters')}
                    </p>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-[#F97700] text-white font-semibold hover:bg-[#E56E00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? t('form.submitting') : t('form.submit')}
                  </button>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="p-4 bg-green-50 border border-green-200 text-green-800 text-sm">
                      {t('form.success')}
                    </div>
                  )}
                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 text-red-800 text-sm">
                      {t('form.error')}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}